import {
  formatJalaliDate,
  formatMileage,
  toPersianDigits,
  toLatinDigits,
} from "../../LatestServices/components/jalaliUtils";
import {
  isNextServiceMileageDue,
  toReminderShape,
} from "../../../services/nextServiceMileage";

export { formatJalaliDate, formatMileage, toPersianDigits, toLatinDigits };

// Notification condition for the explicit Next Service Mileage field:
// due when the vehicle's current mileage reaches or passes it.
// Backend due reminders already encode the same rule
// (currentMileage >= nextDueKm); these helpers make the frontend logic
// explicit, testable, and ready to trigger before backend data arrives.
export {
  isNextServiceMileageDue,
  getNextServiceStatus,
  computeIntervalKm,
  getNextServiceMileageFromDetails,
} from "../../../services/nextServiceMileage";
export { toReminderShape as toLocalReminderShape } from "../../../services/nextServiceMileage";

export function isNextServiceDueForReminder(reminder) {
  if (!reminder) return false;
  return isNextServiceMileageDue(
    reminder.vehicle?.currentMileage,
    reminder.nextDueKm
  );
}

/**
 * Merge backend due reminders with frontend-only Next Service Mileage
 * entries (localStorage mirror) that are due but not yet reflected in the
 * backend response. Backend items win on (vehicleId, categoryId) conflicts.
 * Returns a list in the backend ServiceReminder shape, ready for the
 * existing sort + card components. No fake backend data is created —
 * local items carry `isLocal: true`.
 */

export function getDueReasons(reminder, now = new Date()) {
  if (!reminder) return { isKmDue: false, isDateDue: false };
  const currentMileage = Number(
    reminder.vehicle?.currentMileage ?? NaN
  );
  const isKmDue =
    reminder.nextDueKm !== null &&
    reminder.nextDueKm !== undefined &&
    Number.isFinite(currentMileage) &&
    currentMileage >= Number(reminder.nextDueKm);
  const dueDate = reminder.nextDueDate
    ? new Date(reminder.nextDueDate)
    : null;
  const isDateDue =
    dueDate !== null && !isNaN(dueDate.getTime()) && dueDate <= now;
  return { isKmDue, isDateDue };
}

export function isReminderDue(reminder, now = new Date()) {
  const { isKmDue, isDateDue } = getDueReasons(reminder, now);
  return isKmDue || isDateDue;
}

export function getReasonKind(reminder, now = new Date()) {
  const { isKmDue, isDateDue } = getDueReasons(reminder, now);
  if (isKmDue && isDateDue) return "both";
  if (isKmDue) return "km";
  if (isDateDue) return "date";
  return "upcoming";
}

export function getReasonText(kind) {
  switch (kind) {
    case "both":
      return "موعد کیلومتر و زمان سرویس فرا رسیده است";
    case "km":
      return "کیلومتر سرویس فرا رسیده است";
    case "date":
      return "زمان سرویس فرا رسیده است";
    default:
      return "سرویس نزدیک است";
  }
}

export function getServiceTitle(reminder) {
  return reminder?.category?.title || "سرویس دوره‌ای";
}

export function getVehicleTitle(reminder) {
  return reminder?.vehicle?.title || "خودرو";
}

// Days overdue for date-based sorting (0 when not date-due).
export function getOverdueDays(reminder, now = new Date()) {
  if (!reminder?.nextDueDate) return 0;
  const due = new Date(reminder.nextDueDate);
  if (isNaN(due.getTime()) || due > now) return 0;
  return Math.floor((now.getTime() - due.getTime()) / 86400000);
}

// Km overdue (0 when not km-due).
export function getOverdueKm(reminder) {
  const current = Number(reminder?.vehicle?.currentMileage ?? NaN);
  const due = Number(reminder?.nextDueKm ?? NaN);
  if (!Number.isFinite(current) || !Number.isFinite(due)) return 0;
  return Math.max(0, current - due);
}

// Most urgent first: both-reasons, then larger overdue (days, km),
// then earliest due date.
export function sortNotificationsDesc(list, now = new Date()) {
  const score = (r) => {
    const kind = getReasonKind(r, now);
    const kindRank = kind === "both" ? 0 : kind === "date" ? 1 : kind === "km" ? 2 : 3;
    return { kindRank };
  };
  return [...(list || [])].sort((a, b) => {
    const sa = score(a);
    const sb = score(b);
    if (sa.kindRank !== sb.kindRank) return sa.kindRank - sb.kindRank;
    const daysDiff = getOverdueDays(b, now) - getOverdueDays(a, now);
    if (daysDiff !== 0) return daysDiff;
    const kmDiff = getOverdueKm(b) - getOverdueKm(a);
    if (kmDiff !== 0) return kmDiff;
    const ta = a?.nextDueDate ? new Date(a.nextDueDate).getTime() : Infinity;
    const tb = b?.nextDueDate ? new Date(b.nextDueDate).getTime() : Infinity;
    return (isNaN(ta) ? Infinity : ta) - (isNaN(tb) ? Infinity : tb);
  });
}

// Normalize backend shape errors: API returns { success, data }.
export function normalizeDueResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return null;
}

export function mergeWithLocalNextServiceNotifications({
  backendReminders = [],
  localEntries = [],
  vehiclesById = {},
  categoriesById = {},
} = {}) {
  const backend = Array.isArray(backendReminders) ? backendReminders : [];
  const covered = new Set(
    backend.map((r) => `${r?.vehicleId}::${r?.categoryId}`)
  );
  const locals = (Array.isArray(localEntries) ? localEntries : [])
    .filter((e) => e && !covered.has(`${e.vehicleId}::${e.categoryId}`))
    .map((e) =>
      toReminderShape(e, {
        vehicle: vehiclesById[e.vehicleId] || {
          id: e.vehicleId,
          title: "خودرو",
          currentMileage: null,
        },
        category: categoriesById[e.categoryId] || {
          id: e.categoryId,
          title: "سرویس دوره‌ای",
        },
      })
    )
    .filter(Boolean)
    .filter((r) =>
      isNextServiceMileageDue(r.vehicle?.currentMileage, r.nextDueKm)
    );
  return [...backend, ...locals];
}
